import React from "react";

import DropZoneItemRenderer from "./DropZoneItemRenderer";
import HeaderItemRenderer from "./HeaderItemRenderer";
import MediaItemRenderer from "./MediaItemRenderer";
import {
  OBJECT_TYPES_SCHEDULE_DROPZONE,
  OBJECT_TYPES_SCHEDULE_TEMPLATE,
} from "../../../constants/fast";

import type { JSX, MutableRefObject, RefObject } from "react";
import type { TooltipRefProps } from "react-tooltip";

import type { TimelineRenderTypes } from "./timeline";
import type {
  SchedulingDropzoneItem,
  SchedulingTimelineItem,
  SchedulingTimeslotItem,
  TemplateDropzoneItem,
  TemplateTimelineItem,
  TimelineItemRendererProps,
  TimelineRef,
} from "./timeline.types";
import type { MediaLibraryDragItem } from "../../../types";

export interface OnDropItemParams {
  item: MediaLibraryDragItem;
  startTime: number;
  scheduleId: string | null;
  maxEndTime: number;
}
interface ItemRendererBaseProps {
  dropzoneAcceptedFormats: string[];
  isReplaceMode: boolean;
  timelineRef: MutableRefObject<TimelineRef | null>;
  onDropItem?: ({
    item,
    startTime,
    scheduleId,
    maxEndTime,
  }: OnDropItemParams) => void;
}

/**
 * Items
 */
export interface TemplatingItemRendererProps
  extends
    TimelineItemRendererProps<TemplateTimelineItem>,
    ItemRendererBaseProps {
  onReplaceItem?: (
    itemToDelete: TemplateTimelineItem,
    itemToAdd: MediaLibraryDragItem,
  ) => void;
  onDeleteItem?: (itemId: string) => void;
  renderType: TimelineRenderTypes.TEMPLATING;
}

export interface SchedulingItemRendererProps
  extends
    TimelineItemRendererProps<SchedulingTimelineItem>,
    ItemRendererBaseProps {
  onReplaceItem?: (
    itemToDelete: SchedulingTimelineItem,
    itemToAdd: MediaLibraryDragItem,
  ) => void;
  onDeleteItem?: (itemId: string) => void;
  renderType: TimelineRenderTypes.SCHEDULING;
}

/**
 * Dropzones
 */
export interface TemplatingDropzoneRendererProps extends TimelineItemRendererProps<TemplateDropzoneItem> {
  dropzoneAcceptedFormats: string[];
  isReplaceMode: boolean;
  timelineRef: MutableRefObject<TimelineRef | null>;
  onDropItem?: ({
    item,
    startTime,
    scheduleId,
    maxEndTime,
  }: OnDropItemParams) => void;
  renderType: TimelineRenderTypes.TEMPLATING;
  previewMediaTooltipRef: RefObject<TooltipRefProps | null>;
}

export interface SchedulingDropzoneRendererProps extends TimelineItemRendererProps<SchedulingDropzoneItem> {
  dropzoneAcceptedFormats: string[];
  isReplaceMode: boolean;
  timelineRef: MutableRefObject<TimelineRef | null>;
  onDropItem?: ({
    item,
    startTime,
    scheduleId,
    maxEndTime,
  }: OnDropItemParams) => void;
  previewMediaTooltipRef: RefObject<TooltipRefProps | null>;
  renderType: TimelineRenderTypes.SCHEDULING;
}

/**
 * Template Items
 */
export interface SchedulingTemplateItemRendererProps
  extends
    TimelineItemRendererProps<SchedulingTimeslotItem>,
    ItemRendererBaseProps {
  renderType: TimelineRenderTypes.SCHEDULING;
  timelineRef: MutableRefObject<TimelineRef | null>;
}

type ItemRendererProps =
  | TemplatingItemRendererProps
  | TemplatingDropzoneRendererProps
  | SchedulingItemRendererProps
  | SchedulingDropzoneRendererProps
  | SchedulingTemplateItemRendererProps;

type ConditionalItemRendererProps<T extends ItemRendererProps["item"]["type"]> =
  T extends typeof OBJECT_TYPES_SCHEDULE_TEMPLATE
    ? SchedulingTemplateItemRendererProps
    : T extends typeof OBJECT_TYPES_SCHEDULE_DROPZONE
      ? TemplatingDropzoneRendererProps | SchedulingDropzoneRendererProps
      : TemplatingItemRendererProps | SchedulingItemRendererProps;

function ItemRenderer<T extends ItemRendererProps>(
  props: ConditionalItemRendererProps<T["item"]["type"]>,
): JSX.Element {
  const { itemContext, getItemProps, item, renderType, timelineRef } = props;

  const isDropZone = item.type === OBJECT_TYPES_SCHEDULE_DROPZONE;
  const isItemHeader = item.type === OBJECT_TYPES_SCHEDULE_TEMPLATE;

  if (isItemHeader) {
    return (
      <HeaderItemRenderer
        getItemProps={getItemProps}
        item={item}
        itemContext={itemContext}
        timelineRef={timelineRef}
      />
    );
  }

  if (isDropZone) {
    const {
      dropzoneAcceptedFormats,
      isReplaceMode,
      onDropItem,
      previewMediaTooltipRef,
    } = props as
      | SchedulingDropzoneRendererProps
      | TemplatingDropzoneRendererProps;

    return (
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      <DropZoneItemRenderer
        dropzoneAcceptedFormats={dropzoneAcceptedFormats}
        getItemProps={getItemProps}
        isReplaceMode={isReplaceMode}
        item={item}
        itemContext={itemContext}
        onDropItem={onDropItem}
        previewMediaTooltipRef={previewMediaTooltipRef}
        renderType={renderType}
        timelineRef={timelineRef}
      />
    );
  }
  const {
    dropzoneAcceptedFormats,
    isReplaceMode,
    onReplaceItem,
    onDeleteItem,
  } = props as TemplatingItemRendererProps | SchedulingItemRendererProps;

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <MediaItemRenderer
      dropzoneAcceptedFormats={dropzoneAcceptedFormats}
      getItemProps={getItemProps}
      isReplaceMode={isReplaceMode}
      item={item}
      itemContext={itemContext}
      onDeleteItem={onDeleteItem}
      onReplaceItem={onReplaceItem}
      renderType={renderType}
      timelineRef={timelineRef}
    />
  );
}

export default ItemRenderer;
